import { useHistory } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Elements, ThreeDSecureAction, ThreeDSecureActionProps } from "@recurly/react-recurly";
import { escape } from "lodash";
import { LoadingIndicator } from "@k8slens/lds";

import { useBusinessBillingInformation } from "src/hooks/useBusinessBillingInfo";

import PublicLayout from "src/components/PublicLayout/PublicLayout";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";
import ErrorImage from "src/components/ErrorImage/ErrorImage";
import SuccessImage from "src/components/SuccessImage/SuccessImage";

import { parseBillingInfoQueryParams } from "../../utils";
import { updateSuccessMessage } from "../../page";

import styles from "./page.module.css";

interface ThreeDSAuthenticationProps {
  businessId?: string;
  actionTokenId: string;
}

const ThreeDSAuthentication = ({ businessId, actionTokenId }: ThreeDSAuthenticationProps) => {
  const history = useHistory();
  const billingInfo = useMemo(() => parseBillingInfoQueryParams(history.location), [history.location]);
  const { update, updateSuccess, updating, errorUpdating } = useBusinessBillingInformation(businessId);
  const [waitingForRedirect, setWaitingForRedirect] = useState(false);
  const busy = updating || waitingForRedirect;

  const [error, setError] = useState<string | undefined>();

  const returnUrl = useMemo(() => {
    return escape(`/business/${businessId}/billing-details`);
  }, [businessId]);

  const updateUrl = useMemo(() => {
    return escape(`/business/${businessId}/billing-details/update`);
  }, [businessId]);

  useEffect(() => {
    if (!billingInfo || !billingInfo.token || !billingInfo.paymentMethod) {
      setError("Billing information is not available.");
    } else {
      setError(undefined);
    }
  }, [billingInfo]);

  const handleToken = useCallback(
    async (token: Parameters<NonNullable<ThreeDSecureActionProps["onToken"]>>[0]) => {
      if (!billingInfo) {
        return;
      }
      setError(undefined);

      update({
        ...billingInfo,
        token: billingInfo.token!,
        paymentMethod: billingInfo.paymentMethod!,
        type: "credit_card",
        threeDSecureActionResultTokenId: token.id,
      });
    },
    [billingInfo, update],
  );

  useEffect(() => {
    let mounted = true;

    if (updateSuccess) {
      setWaitingForRedirect(true);
      // Wait for 2 seconds to allow the user to see the success message
      new Promise((resolve) => setTimeout(resolve, 2000)).then(() => {
        if (mounted) {
          history.replace(returnUrl);
        }
      });

      return () => {
        mounted = false;
      };
    }
  }, [updateSuccess, returnUrl, history]);

  const handleError = useCallback((error: Error) => {
    setError(error.message);
  }, []);

  const errorMessage = error || errorUpdating?.message;
  const successMessage = updateSuccess ? updateSuccessMessage : undefined;

  return (
    <PublicLayout
      title="3D Secure Authentication"
      notification={errorMessage || successMessage}
      notificationProps={successMessage ? { level: "success", type: "flash" } : { level: "error", type: "flash" }}
      panelProps={{
        footerComponent: "footer",
        footerProps: { className: styles.footer },
        contentProps: { className: styles.content },
      }}
      footer={
        error || errorUpdating?.message ? (
          <TrackedAnchor href={updateUrl} className={styles.link}>
            Back to Billing Information page
          </TrackedAnchor>
        ) : null
      }
    >
      {!(errorMessage && successMessage) ? (
        <ThreeDSecureAction actionTokenId={actionTokenId} onToken={handleToken} onError={handleError} />
      ) : null}
      {errorMessage ? <ErrorImage /> : null}
      {successMessage ? <SuccessImage /> : null}
      {busy || successMessage ? <LoadingIndicator size="2xl" /> : null}
    </PublicLayout>
  );
};

const Wrapper = ({ actionTokenId, businessId }: ThreeDSAuthenticationProps) => {
  return (
    <Elements>
      <ThreeDSAuthentication actionTokenId={actionTokenId} businessId={businessId} />
    </Elements>
  );
};

export default Wrapper;
